/**
 * Authored keys for stems the paper and Leo reuse.
 *
 * Preview used to pick a correct letter from a hash. Cardiology then marked
 * Purkinje fibres on a sinus-rhythm item. Known stems carry a real key.
 */

export type AuthoredChoice = {
  text: string
  correct: boolean
}

export type AuthoredItem = {
  title: string
  stem: string
  blooms: string
  difficulty: "Easy" | "Medium" | "Hard"
  choices: AuthoredChoice[]
}

function norm(text: string): string {
  return text.trim().toLowerCase().replace(/fibres/g, "fibers")
}

const TELEMETRY_PACEMAKER_STEM =
  /telemetry strip is regular at 72\/min with a p wave before each qrs/i

export function isTelemetryPacemakerStem(stem: string): boolean {
  return TELEMETRY_PACEMAKER_STEM.test(stem)
}

/** Current paper item. D is the sinoatrial node. */
export const PACEMAKER_TELEMETRY: AuthoredItem = {
  title: "Sinus pacemaker",
  stem: "A telemetry strip is regular at 72/min with a P wave before each QRS. Which structure is setting this rate?",
  blooms: "Remembering",
  difficulty: "Easy",
  choices: [
    { text: "Bundle of His", correct: false },
    { text: "Purkinje fibres", correct: false },
    { text: "Left bundle branch", correct: false },
    { text: "Sinoatrial node", correct: true },
  ],
}

/** Leo replace for the telemetry pacemaker item. */
export const PACEMAKER_ATHLETE: AuthoredItem = {
  title: "Sinus impulse source",
  stem: "A 22-year-old athlete undergoes a routine pre-participation cardiac screening. A 12-lead electrocardiogram demonstrates a regular rhythm at 68 beats/min. Each narrow QRS complex is preceded by an upright P wave with a constant PR interval of 0.16 seconds.\nWhich structure is the primary source of electrical impulse generation in this patient?",
  blooms: "Remembering",
  difficulty: "Easy",
  choices: [
    { text: "Atrioventricular node", correct: false },
    { text: "Bundle of His", correct: false },
    { text: "Purkinje network", correct: false },
    { text: "Sinoatrial node", correct: true },
  ],
}

export const PACEMAKER_HEART_BLOCK: AuthoredItem = {
  title: "Ventricular escape pacemaker",
  stem: "A 74-year-old patient with severe calcific aortic stenosis develops acute dizziness and fatigue. A continuous telemetry tracing reveals:\nIndependent atrial contractions at 75 beats/min (uniform P waves)\nDissociated, regular ventricular contractions at 32 beats/min\nBroad, abnormally wide QRS complexes (duration: 0.16 seconds)\nWhich cardiac structure is responsible for setting the ventricular rate in this patient?",
  blooms: "Applying",
  difficulty: "Medium",
  choices: [
    { text: "Atrioventricular node", correct: false },
    { text: "Bundle of His", correct: false },
    { text: "Purkinje fibers", correct: true },
    { text: "Sinoatrial node", correct: false },
  ],
}

export const PACEMAKER_JUNCTIONAL: AuthoredItem = {
  title: "Junctional escape rhythm",
  stem: "A 68-year-old becomes lightheaded. Telemetry shows a regular rhythm at 46/min with no visible P waves and a narrow QRS of 0.08 seconds.\nWhich structure is most likely generating this rhythm?",
  blooms: "Applying",
  difficulty: "Medium",
  choices: [
    { text: "Sinoatrial node", correct: false },
    { text: "Atrioventricular node", correct: true },
    { text: "Bundle of His", correct: false },
    { text: "Purkinje fibers", correct: false },
  ],
}

const PACEMAKER_REPLACE = PACEMAKER_ATHLETE
const PACEMAKER_SUGGESTIONS = [PACEMAKER_HEART_BLOCK, PACEMAKER_JUNCTIONAL] as const

const CATALOG: { match: RegExp; item: AuthoredItem }[] = [
  { match: TELEMETRY_PACEMAKER_STEM, item: PACEMAKER_TELEMETRY },
  { match: /22-year-old athlete|pre-participation cardiac screening/i, item: PACEMAKER_ATHLETE },
  { match: /calcific aortic stenosis|independent atrial contractions/i, item: PACEMAKER_HEART_BLOCK },
  { match: /no visible p waves and a narrow qrs/i, item: PACEMAKER_JUNCTIONAL },
  {
    match: /HFpEF is sitting bolt upright and coughing pink frothy sputum/i,
    item: {
      title: "HFpEF flash edema",
      stem: "A 74-year-old with HFpEF is sitting bolt upright and coughing pink frothy sputum. Which assessment is the priority?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Airway and oxygenation", correct: true },
        { text: "Daily weight trend", correct: false },
        { text: "Ankle edema measurement", correct: false },
        { text: "Home medication list", correct: false },
      ],
    },
  },
  {
    match: /after a pelvic fracture, pulse is 128/i,
    item: {
      title: "Hypovolemia compensation",
      stem: "After a pelvic fracture, pulse is 128, BP is 88/54, and the skin is cool and clammy. Which compensatory response explains these findings?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "Sympathetic vasoconstriction", correct: true },
        { text: "Increased urine output", correct: false },
        { text: "Parasympathetic slowing of the heart", correct: false },
        { text: "Peripheral vasodilation", correct: false },
      ],
    },
  },
  {
    match: /st elevation in ii, iii, and avf/i,
    item: {
      title: "Inferior STEMI territory",
      stem: "Chest pain started 30 minutes ago. The ECG shows ST elevation in II, III, and aVF. Which coronary territory is ischemic?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Inferior wall, right coronary artery", correct: true },
        { text: "Anterior wall, left anterior descending artery", correct: false },
        { text: "Lateral wall, circumflex artery", correct: false },
        { text: "Posterior wall, left main artery", correct: false },
      ],
    },
  },
  {
    match: /six hours after an anterior mi, bp is 78\/50/i,
    item: {
      title: "Cardiogenic shock",
      stem: "Six hours after an anterior MI, BP is 78/50, crackles reach the apices, and the legs are mottled. Which problem is treated first?",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Pump failure", correct: true },
        { text: "Volume overload from oral fluids", correct: false },
        { text: "Anxiety", correct: false },
        { text: "Pain at the IV site", correct: false },
      ],
    },
  },
  {
    match: /s2 splits on inspiration and becomes single on expiration/i,
    item: {
      title: "Physiologic split S2",
      stem: "S2 splits on inspiration and becomes single on expiration. What does this finding indicate?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "A normal physiologic split", correct: true },
        { text: "Fixed splitting from an atrial septal defect", correct: false },
        { text: "Paradoxical splitting from left bundle branch block", correct: false },
        { text: "Aortic stenosis", correct: false },
      ],
    },
  },
  {
    match: /harsh systolic murmur is loudest at the right upper sternal border/i,
    item: {
      title: "Aortic stenosis murmur",
      stem: "A harsh systolic murmur is loudest at the right upper sternal border and radiates to the carotids. Which valve lesion produces this sound?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Aortic stenosis", correct: true },
        { text: "Mitral regurgitation", correct: false },
        { text: "Aortic regurgitation", correct: false },
        { text: "Mitral stenosis", correct: false },
      ],
    },
  },
  {
    match: /slow-rising carotid pulse and fainted while climbing stairs/i,
    item: {
      title: "Delayed carotid upstroke",
      stem: "A 79-year-old has a slow-rising carotid pulse and fainted while climbing stairs. Which valvular lesion is most likely?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Aortic stenosis", correct: true },
        { text: "Mitral stenosis", correct: false },
        { text: "Tricuspid regurgitation", correct: false },
        { text: "Pulmonic regurgitation", correct: false },
      ],
    },
  },
  {
    match: /warfarin 5 mg nightly/i,
    item: {
      title: "Warfarin monitoring",
      stem: "A client takes warfarin 5 mg nightly. Which laboratory value is used to adjust tomorrow's dose?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "INR", correct: true },
        { text: "aPTT", correct: false },
        { text: "Serum potassium", correct: false },
        { text: "Hemoglobin A1c", correct: false },
      ],
    },
  },
  {
    match: /two minutes after im epinephrine, which finding shows the dose is working/i,
    item: {
      title: "Epinephrine response",
      stem: "A client in anaphylaxis has wheezing and a BP of 72/40. Two minutes after IM epinephrine, which finding shows the dose is working?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Blood pressure rises and wheezing lessens", correct: true },
        { text: "Heart rate falls to 40", correct: false },
        { text: "The client becomes more drowsy", correct: false },
        { text: "Skin becomes cooler and mottled", correct: false },
      ],
    },
  },
  {
    match: /lisinopril was started 3 days ago/i,
    item: {
      title: "ACE inhibitor potassium",
      stem: "Lisinopril was started 3 days ago. Today's potassium is 5.6 mEq/L. Which action is indicated?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Hold the dose and notify the prescriber", correct: true },
        { text: "Give the usual dose with orange juice", correct: false },
        { text: "Double tomorrow's dose", correct: false },
        { text: "Switch to a potassium-sparing diuretic", correct: false },
      ],
    },
  },
  {
    match: /antibiotic classes weaken the bacterial cell wall/i,
    item: {
      title: "Cell wall antibiotics",
      stem: "Which antibiotic classes weaken the bacterial cell wall? Select all that apply.",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Penicillins", correct: true },
        { text: "Cephalosporins", correct: true },
        { text: "Macrolides", correct: false },
        { text: "Aminoglycosides", correct: false },
      ],
    },
  },
  {
    match: /digoxin reports yellow halos around lights/i,
    item: {
      title: "Digoxin toxicity",
      stem: "A client on digoxin reports yellow halos around lights and a pulse of 46. Which action is the priority?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Hold digoxin and notify the prescriber", correct: true },
        { text: "Give the dose with food", correct: false },
        { text: "Encourage a walk in the hallway", correct: false },
        { text: "Document the visual change only", correct: false },
      ],
    },
  },
  {
    match: /clarithromycin is added for a client who takes simvastatin/i,
    item: {
      title: "CYP3A4 interaction",
      stem: "Clarithromycin is added for a client who takes simvastatin. Which finding is the earliest sign of a dangerous interaction?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "New muscle pain or weakness", correct: true },
        { text: "Dry cough", correct: false },
        { text: "Metallic taste", correct: false },
        { text: "Mild nasal congestion", correct: false },
      ],
    },
  },
  {
    match: /egfr 18 ml\/min is prescribed vancomycin/i,
    item: {
      title: "Renal dose adjustment",
      stem: "A client with eGFR 18 mL/min is prescribed vancomycin 1 g IV every 8 hours. Which action is indicated?",
      blooms: "Applying",
      difficulty: "Hard",
      choices: [
        { text: "Hold and ask pharmacy to adjust the interval", correct: true },
        { text: "Give the usual adult schedule", correct: false },
        { text: "Double the next dose", correct: false },
        { text: "Switch to an oral form only", correct: false },
      ],
    },
  },
  {
    match: /furosemide 40 mg iv is ordered for flash pulmonary edema/i,
    item: {
      title: "Loop diuretic site",
      stem: "Furosemide 40 mg IV is ordered for flash pulmonary edema. Where in the nephron does this drug act?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Thick ascending limb of the loop of Henle", correct: true },
        { text: "Proximal convoluted tubule only", correct: false },
        { text: "Collecting duct principal cells", correct: false },
        { text: "Glomerular capillaries", correct: false },
      ],
    },
  },
  {
    match: /started amiodarone 2 weeks ago has a dry cough/i,
    item: {
      title: "Amiodarone lung toxicity",
      stem: "A client who started amiodarone 2 weeks ago has a dry cough and new dyspnea at rest. Which action is indicated?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Hold amiodarone and notify the prescriber", correct: true },
        { text: "Encourage incentive spirometry only", correct: false },
        { text: "Increase the next dose", correct: false },
        { text: "Give a cough suppressant and reassess tomorrow", correct: false },
      ],
    },
  },
  {
    match: /hold the evening warfarin dose when today's inr/i,
    item: {
      title: "Warfarin hold rule",
      stem: "Hold the evening warfarin dose when today's INR is above the ordered range.",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "True", correct: true },
        { text: "False", correct: false },
      ],
    },
  },
  {
    match: /temperature of 38\.9 c, bp 86\/48, and a new holosystolic murmur/i,
    item: {
      title: "Fever hypotension murmur",
      stem: "A 61-year-old has a temperature of 38.9 C, BP 86/48, and a new holosystolic murmur. Which action is the priority?",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Obtain blood cultures and start fluids", correct: true },
        { text: "Schedule an elective echo next week", correct: false },
        { text: "Discharge with oral antipyretics", correct: false },
        { text: "Begin physical therapy", correct: false },
      ],
    },
  },
  {
    match: /fever, hypotension, and a new murmur:?$/i,
    item: {
      title: "Sepsis cue",
      stem: "A 61-year-old has fever, hypotension, and a new murmur:",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Obtain blood cultures and start fluids", correct: true },
        { text: "Schedule an elective echo next week", correct: false },
        { text: "Discharge with oral antipyretics", correct: false },
        { text: "Begin physical therapy", correct: false },
      ],
    },
  },
  {
    match: /systolic bp of 78 after a fluid bolus/i,
    item: {
      title: "Escalate first finding",
      stem: "A client has a temperature of 38.1 C, heart rate 102, respiratory rate 22, and a systolic BP of 78 after a fluid bolus. Which finding is treated first?",
      blooms: "Evaluating",
      difficulty: "Medium",
      choices: [
        { text: "Temperature 38.1 C", correct: false },
        { text: "Systolic blood pressure 78 mmHg after fluids", correct: true },
        { text: "Heart rate 102", correct: false },
        { text: "Respiratory rate 22", correct: false },
      ],
    },
  },
  {
    match: /chest pain is now 4\/10 after nitroglycerin, troponin is pending/i,
    item: {
      title: "Next diagnostic step",
      stem: "Chest pain is now 4/10 after nitroglycerin, troponin is pending, and the ECG is unchanged. What is the next diagnostic step?",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Wait for the troponin and repeat the ECG", correct: true },
        { text: "Discharge without further labs", correct: false },
        { text: "Start thrombolysis now", correct: false },
        { text: "Cancel telemetry", correct: false },
      ],
    },
  },
  {
    match: /bp falls from 118\/72 to 86\/50 and the client becomes newly confused/i,
    item: {
      title: "Rapid response trigger",
      stem: "Over 20 minutes BP falls from 118/72 to 86/50 and the client becomes newly confused. Which action is indicated?",
      blooms: "Evaluating",
      difficulty: "Medium",
      choices: [
        { text: "Activate the rapid response team", correct: true },
        { text: "Document and recheck in one hour", correct: false },
        { text: "Encourage ambulation", correct: false },
        { text: "Give the scheduled hypnotic", correct: false },
      ],
    },
  },
  {
    match: /vomiting for 18 hours, is breathing deep and fast, and has a fruity odor/i,
    item: {
      title: "DKA acid-base",
      stem: "A client with type 1 diabetes has been vomiting for 18 hours, is breathing deep and fast, and has a fruity odor on the breath. Which acid-base change is expected?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Metabolic acidosis with compensatory hyperventilation", correct: true },
        { text: "Metabolic alkalosis from vomiting alone", correct: false },
        { text: "Respiratory acidosis from hypoventilation", correct: false },
        { text: "Normal acid-base balance", correct: false },
      ],
    },
  },
  {
    match: /all of the following are expected after iv furosemide, except/i,
    item: {
      title: "Loop diuretic teaching",
      stem: "All of the following are expected after IV furosemide, EXCEPT;",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "They raise serum potassium", correct: true },
        { text: "They act on the thick ascending limb", correct: false },
        { text: "They can reduce preload", correct: false },
        { text: "They increase urine output", correct: false },
      ],
    },
  },
  {
    match: /nursing student faints after standing for morning report/i,
    item: {
      title: "Orthostatic syncope",
      stem: "A 22-year-old nursing student faints after standing for morning report. Which finding most likely explains the syncope?",
      blooms: "Analyzing",
      difficulty: "Hard",
      choices: [
        { text: "Transient drop in venous return", correct: true },
        { text: "Acute ST elevation MI", correct: false },
        { text: "Complete heart block", correct: false },
        { text: "Massive pulmonary embolism", correct: false },
      ],
    },
  },
  {
    match: /priority before a first-year student administers subcutaneous insulin/i,
    item: {
      title: "Insulin teaching",
      stem: "Which teaching point is the priority before a first-year student administers subcutaneous insulin?",
      blooms: "Applying",
      difficulty: "Hard",
      choices: [
        { text: "Confirm the dose and check the glucose first", correct: true },
        { text: "Give the injection through clothing", correct: false },
        { text: "Shake the vial until it foams", correct: false },
        { text: "Recap a used needle for later", correct: false },
      ],
    },
  },
  {
    match: /postoperative patient on morphine has a respiratory rate of 6/i,
    item: {
      title: "Opioid hypoventilation",
      stem: "A postoperative patient on morphine has a respiratory rate of 6. What is the priority action?",
      blooms: "Applying",
      difficulty: "Hard",
      choices: [
        { text: "Support the airway and give naloxone", correct: true },
        { text: "Give the next morphine dose early", correct: false },
        { text: "Encourage a long walk", correct: false },
        { text: "Document and reassess in two hours", correct: false },
      ],
    },
  },
  {
    match: /laboratory trend most strongly suggests acute kidney injury after a contrast/i,
    item: {
      title: "Contrast AKI",
      stem: "Which laboratory trend most strongly suggests acute kidney injury after a contrast study?",
      blooms: "Analyzing",
      difficulty: "Hard",
      choices: [
        { text: "Rising serum creatinine", correct: true },
        { text: "Falling troponin", correct: false },
        { text: "Stable hemoglobin", correct: false },
        { text: "Low random glucose", correct: false },
      ],
    },
  },
  {
    match: /new facial droop and slurred speech that started 20 minutes ago/i,
    item: {
      title: "Stroke first action",
      stem: "A patient reports new facial droop and slurred speech that started 20 minutes ago. What is the first action?",
      blooms: "Applying",
      difficulty: "Hard",
      choices: [
        { text: "Activate the stroke pathway now", correct: true },
        { text: "Wait to see if speech returns", correct: false },
        { text: "Give aspirin at home and reassess tomorrow", correct: false },
        { text: "Send the client to a routine clinic", correct: false },
      ],
    },
  },
  {
    match: /isolation precaution is required for a patient with suspected clostridioides difficile/i,
    item: {
      title: "C. difficile isolation",
      stem: "Which isolation precaution is required for a patient with suspected Clostridioides difficile?",
      blooms: "Remembering",
      difficulty: "Hard",
      choices: [
        { text: "Contact precautions", correct: true },
        { text: "Airborne precautions only", correct: false },
        { text: "Protective isolation", correct: false },
        { text: "No isolation is required", correct: false },
      ],
    },
  },
  {
    match: /select the receptors that explain why im epinephrine/i,
    item: {
      title: "Epinephrine receptors",
      stem: "A client in anaphylaxis has wheezing and a BP of 72/40. Select the receptors that explain why IM epinephrine opens the airway and raises BP.",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Alpha-1 adrenergic", correct: true },
        { text: "Beta-2 adrenergic", correct: true },
        { text: "H1 histamine", correct: false },
        { text: "Muscarinic acetylcholine", correct: false },
      ],
    },
  },
  {
    match: /fat-soluble vitamins should be taken with food/i,
    item: {
      title: "Fat-soluble vitamins",
      stem: "A client is starting vitamins at home. Which fat-soluble vitamins should be taken with food? Select all that apply.",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Vitamin A", correct: true },
        { text: "Vitamin C", correct: false },
        { text: "Vitamin D", correct: true },
        { text: "Vitamin B12", correct: false },
      ],
    },
  },
  {
    match: /which enzymes belong to glycolysis/i,
    item: {
      title: "Glycolysis enzymes",
      stem: "Which enzymes belong to glycolysis? Select all that apply.",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Hexokinase", correct: true },
        { text: "Pyruvate kinase", correct: true },
        { text: "Citrate synthase", correct: false },
        { text: "Catalase", correct: false },
      ],
    },
  },
  {
    match: /two hours post abdominal surgery has a blood pressure of 88\/52/i,
    item: {
      title: "Postop hypovolemia",
      stem: "A client two hours post abdominal surgery has a blood pressure of 88/52, a heart rate of 118, and cool clammy skin. Which action does the nurse take first?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Raise the legs and give oxygen while notifying the provider", correct: true },
        { text: "Document the readings and recheck in one hour", correct: false },
        { text: "Encourage oral fluids and ambulation", correct: false },
        { text: "Give the scheduled opioid to settle the client", correct: false },
      ],
    },
  },
  {
    match: /finding in a client receiving furosemide most needs the nurse to hold/i,
    item: {
      title: "Hold loop diuretic",
      stem: "Which finding in a client receiving furosemide most needs the nurse to hold the dose and call the provider?",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Urine output of 60 mL per hour", correct: false },
        { text: "Serum potassium of 2.9 mEq/L", correct: true },
        { text: "Weight loss of 0.5 kg since yesterday", correct: false },
        { text: "Reported thirst", correct: false },
      ],
    },
  },
  {
    match: /laboratory value most warrants holding a dose of furosemide/i,
    item: {
      title: "Holding a loop diuretic",
      stem: "Which laboratory value most warrants holding a dose of furosemide?",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Urine output of 60 mL per hour", correct: false },
        { text: "Serum potassium of 2.9 mEq/L", correct: true },
        { text: "Weight loss of half a kilogram overnight", correct: false },
        { text: "Client-reported thirst", correct: false },
      ],
    },
  },
  {
    match: /entries belong in the objective section of a nursing note/i,
    item: {
      title: "Objective charting",
      stem: "Which entries belong in the objective section of a nursing note? Select all that apply.",
      blooms: "Understanding",
      difficulty: "Easy",
      choices: [
        { text: "Temperature of 38.4 degrees Celsius", correct: true },
        { text: "Wound edges approximated with scant serous drainage", correct: true },
        { text: "The client's report of throbbing pain", correct: false },
        { text: "Bilateral crackles on auscultation", correct: true },
      ],
    },
  },
  {
    match: /stage 2 pressure injury extends through the full thickness/i,
    item: {
      title: "Stage 2 pressure injury",
      stem: "A stage 2 pressure injury extends through the full thickness of the dermis into subcutaneous fat.",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "True", correct: false },
        { text: "False", correct: true },
      ],
    },
  },
  {
    match: /copd desaturates to 86% while walking to the bathroom/i,
    item: {
      title: "COPD desaturation",
      stem: "A client with COPD desaturates to 86% while walking to the bathroom. The most appropriate nursing response is:",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Stop, seat the client, and reassess with pursed-lip breathing", correct: true },
        { text: "Increase the pace to build tolerance", correct: false },
        { text: "Continue and document the reading", correct: false },
        { text: "Remove oxygen for a trial off", correct: false },
      ],
    },
  },
  {
    match: /whether fluid replacement is working in a dehydrated client/i,
    item: {
      title: "Fluid replacement response",
      stem: "Which assessment best tells the nurse whether fluid replacement is working in a dehydrated client?",
      blooms: "Applying",
      difficulty: "Easy",
      choices: [
        { text: "Hourly urine output", correct: true },
        { text: "Skin turgor over the sternum", correct: false },
        { text: "Reported thirst", correct: false },
        { text: "Oral mucous membranes", correct: false },
      ],
    },
  },
  {
    match: /older (client|adult) is admitted after a fall/i,
    item: {
      title: "Fall risk on admission",
      stem: "An older client is admitted after a fall at home. Which action does the nurse take first on arrival to the unit?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Complete a fall risk assessment", correct: true },
        { text: "Order a walking frame", correct: false },
        { text: "Teach home hazard removal", correct: false },
        { text: "Refer to physiotherapy", correct: false },
      ],
    },
  },
  {
    match: /findings after a blood transfusion begins require the nurse to stop/i,
    item: {
      title: "Transfusion reaction",
      stem: "Which findings after a blood transfusion begins require the nurse to stop the infusion? Select all that apply.",
      blooms: "Analyzing",
      difficulty: "Medium",
      choices: [
        { text: "Temperature rise of 1.2 degrees Celsius", correct: true },
        { text: "Flank pain with dark urine", correct: true },
        { text: "Mild anxiety about the needle", correct: false },
        { text: "New wheeze and facial flushing", correct: true },
      ],
    },
  },
  {
    match: /before giving digoxin, the nurse checks which parameter/i,
    item: {
      title: "Digoxin apical rate",
      stem: "Before giving digoxin, the nurse checks which parameter?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Apical heart rate for one full minute", correct: true },
        { text: "Respiratory rate", correct: false },
        { text: "Oral temperature", correct: false },
        { text: "Pupil reaction", correct: false },
      ],
    },
  },
  {
    match: /match each bedside presentation with the most likely diagnosis/i,
    item: {
      title: "Presentation matching",
      stem: "Match each bedside presentation with the most likely diagnosis.",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "Scurvy with vitamin C, rickets with vitamin D, goiter with iodine", correct: true },
        { text: "Scurvy with vitamin D, rickets with vitamin C, goiter with iodine", correct: false },
        { text: "Scurvy with iodine, rickets with vitamin D, goiter with vitamin C", correct: false },
        { text: "Scurvy with vitamin C, rickets with iodine, goiter with vitamin D", correct: false },
      ],
    },
  },
  {
    match: /patient with new heart failure reports dyspnea\. which nursing assessment is the priority/i,
    item: {
      title: "Heart failure nursing priority",
      stem: "A patient with new heart failure reports dyspnea. Which nursing assessment is the priority?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "Lung sounds and oxygenation", correct: true },
        { text: "Home medication list only", correct: false },
        { text: "Ankle circumference", correct: false },
        { text: "Preferred meal times", correct: false },
      ],
    },
  },
  {
    match: /which symptom of angina should the nurse teach a patient to report/i,
    item: {
      title: "Angina symptoms",
      stem: "Which symptom of angina should the nurse teach a patient to report?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Chest pressure that is not relieved by rest", correct: true },
        { text: "Mild thirst after dinner", correct: false },
        { text: "A single premature beat", correct: false },
        { text: "Warm feet after walking", correct: false },
      ],
    },
  },
  {
    match: /most common trigger of myocardial infarction/i,
    item: {
      title: "Leading cause of MI",
      stem: "Which cause should the nurse recognize as the most common trigger of myocardial infarction?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Atherosclerotic plaque rupture with thrombosis", correct: true },
        { text: "A high-potassium meal", correct: false },
        { text: "Seasonal allergies", correct: false },
        { text: "A single missed dose of a vitamin", correct: false },
      ],
    },
  },
  {
    match: /ecg finding should the nurse treat as an acute stemi/i,
    item: {
      title: "ECG signs of acute STEMI",
      stem: "Which ECG finding should the nurse treat as an acute STEMI?",
      blooms: "Applying",
      difficulty: "Hard",
      choices: [
        { text: "ST elevation in two contiguous leads", correct: true },
        { text: "A single premature ventricular complex", correct: false },
        { text: "Sinus arrhythmia", correct: false },
        { text: "First-degree AV delay only", correct: false },
      ],
    },
  },
  {
    match: /early finding should the nurse associate with parkinson disease/i,
    item: {
      title: "Early Parkinson's signs",
      stem: "Which early finding should the nurse associate with Parkinson disease?",
      blooms: "Remembering",
      difficulty: "Medium",
      choices: [
        { text: "Resting tremor and bradykinesia", correct: true },
        { text: "Sudden flaccid paralysis", correct: false },
        { text: "Fever with nuchal rigidity", correct: false },
        { text: "Unilateral hearing loss only", correct: false },
      ],
    },
  },
  {
    match: /deficit should the nurse anticipate after frontal lobe injury/i,
    item: {
      title: "Frontal lobe function",
      stem: "Which deficit should the nurse anticipate after frontal lobe injury?",
      blooms: "Understanding",
      difficulty: "Hard",
      choices: [
        { text: "Personality change and poor judgment", correct: true },
        { text: "Cortical blindness", correct: false },
        { text: "Loss of hearing only", correct: false },
        { text: "Inability to feel vibration in the toes", correct: false },
      ],
    },
  },
  {
    match: /finding should the nurse associate with programmed cell death/i,
    item: {
      title: "Apoptosis definition",
      stem: "Which finding should the nurse associate with programmed cell death?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Apoptosis without inflammation", correct: true },
        { text: "Liquefactive necrosis", correct: false },
        { text: "Purulent exudate", correct: false },
        { text: "Caseating granulomas", correct: false },
      ],
    },
  },
  {
    match: /tissue change should the nurse recognize as necrosis/i,
    item: {
      title: "Stages of necrosis",
      stem: "Which tissue change should the nurse recognize as necrosis?",
      blooms: "Remembering",
      difficulty: "Medium",
      choices: [
        { text: "Unregulated cell death with inflammation", correct: true },
        { text: "Reversible mitochondrial swelling only", correct: false },
        { text: "Programmed cell shrinkage without inflammation", correct: false },
        { text: "Hypertrophy of surviving cells", correct: false },
      ],
    },
  },
  {
    match: /how should the nurse explain the action of aspirin to a patient/i,
    item: {
      title: "Aspirin mechanism",
      stem: "How should the nurse explain the action of aspirin to a patient?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "It reduces platelet clumping so clots are less likely", correct: true },
        { text: "It dissolves an existing coronary clot instantly", correct: false },
        { text: "It raises the resting heart rate", correct: false },
        { text: "It replaces lost clotting factors", correct: false },
      ],
    },
  },
  {
    match: /route should the nurse expect to produce the fastest systemic effect/i,
    item: {
      title: "Fastest route of administration",
      stem: "Which route should the nurse expect to produce the fastest systemic effect?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Intravenous", correct: true },
        { text: "Oral tablet", correct: false },
        { text: "Transdermal patch", correct: false },
        { text: "Rectal suppository", correct: false },
      ],
    },
  },
  {
    match: /cellular finding should the nurse associate with mitochondrial failure/i,
    item: {
      title: "Mitochondrial function",
      stem: "Which cellular finding should the nurse associate with mitochondrial failure?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Falling ATP production", correct: true },
        { text: "Faster aerobic respiration", correct: false },
        { text: "Excess stored glycogen only", correct: false },
        { text: "Thicker myelin", correct: false },
      ],
    },
  },
  {
    match: /first step of the nursing process/i,
    item: {
      title: "Nursing process first step",
      stem: "Which action is the first step of the nursing process?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Assessment", correct: true },
        { text: "Implementation", correct: false },
        { text: "Evaluation", correct: false },
        { text: "Delegation", correct: false },
      ],
    },
  },
  {
    match: /crushing chest pain\. which ecg finding should the nurse treat as an acute stemi/i,
    item: {
      title: "STEMI ECG progression",
      stem: "A patient presents with crushing chest pain. Which ECG finding should the nurse treat as an acute STEMI needing immediate reperfusion?",
      blooms: "Analyzing",
      difficulty: "Hard",
      choices: [
        { text: "ST elevation in two contiguous leads", correct: true },
        { text: "Isolated T-wave flattening", correct: false },
        { text: "A single PAC", correct: false },
        { text: "Sinus bradycardia at 58", correct: false },
      ],
    },
  },
  {
    match: /diuretic should the nurse expect first for volume overload in acute decompensated heart failure/i,
    item: {
      title: "Heart failure diuretic choice",
      stem: "Which diuretic should the nurse expect first for volume overload in acute decompensated heart failure?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Intravenous loop diuretic", correct: true },
        { text: "Oral thiazide only", correct: false },
        { text: "Potassium-sparing diuretic as monotherapy", correct: false },
        { text: "Osmotic diuretic", correct: false },
      ],
    },
  },
  {
    match: /new atrial fibrillation and rapid ventricular response, which agent should the nurse anticipate for rate control/i,
    item: {
      title: "AF rate control agent",
      stem: "For a hemodynamically stable patient with new atrial fibrillation and rapid ventricular response, which agent should the nurse anticipate for rate control?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "A beta blocker or nondihydropyridine calcium-channel blocker", correct: true },
        { text: "Immediate unsynchronized defibrillation", correct: false },
        { text: "A high-dose loop diuretic only", correct: false },
        { text: "Subcutaneous heparin alone", correct: false },
      ],
    },
  },
  {
    match: /antiplatelet regimen should the nurse prepare before pci in nste-acs/i,
    item: {
      title: "ACS antiplatelet loading",
      stem: "Which antiplatelet regimen should the nurse prepare before PCI in NSTE-ACS?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Aspirin plus a P2Y12 inhibitor", correct: true },
        { text: "Warfarin monotherapy", correct: false },
        { text: "Aspirin stopped for five days", correct: false },
        { text: "A glycoprotein IIb/IIIa infusion only, with no aspirin", correct: false },
      ],
    },
  },
  {
    match: /clinical finding should the nurse treat as an mca territory ischemic stroke/i,
    item: {
      title: "Stroke territory signs",
      stem: "Which clinical finding should the nurse treat as an MCA territory ischemic stroke?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "Contralateral hemiparesis with aphasia or neglect", correct: true },
        { text: "Crossed findings in the face and body", correct: false },
        { text: "Isolated ataxia without weakness", correct: false },
        { text: "Bilateral pinpoint pupils only", correct: false },
      ],
    },
  },
  {
    match: /receptor should the nurse recall when a beta blocker lowers heart rate/i,
    item: {
      title: "Beta blocker primary action",
      stem: "Which receptor should the nurse recall when a beta blocker lowers heart rate?",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Beta-1 adrenergic receptors", correct: true },
        { text: "Alpha-1 adrenergic receptors", correct: false },
        { text: "Muscarinic receptors in the gut", correct: false },
        { text: "H1 histamine receptors", correct: false },
      ],
    },
  },
  {
    match: /oral agent should the nurse anticipate for gradual blood-pressure reduction in hypertensive urgency/i,
    item: {
      title: "Hypertensive urgency oral agent",
      stem: "Which oral agent should the nurse anticipate for gradual blood-pressure reduction in hypertensive urgency without end-organ damage?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Oral labetalol or captopril", correct: true },
        { text: "Immediate IV nitroprusside", correct: false },
        { text: "A fluid bolus", correct: false },
        { text: "Sublingual nifedipine crushed for a rapid drop", correct: false },
      ],
    },
  },
  {
    match: /after an uncomplicated mi, which statin intensity should the nurse expect/i,
    item: {
      title: "Statin intensity after MI",
      stem: "After an uncomplicated MI, which statin intensity should the nurse expect for secondary prevention?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "High-intensity statin therapy", correct: true },
        { text: "No lipid therapy after one event", correct: false },
        { text: "A bile-acid sequestrant only", correct: false },
        { text: "Low-intensity statin for two days", correct: false },
      ],
    },
  },
  {
    match: /triad should the nurse recognize as idiopathic parkinson disease/i,
    item: {
      title: "Parkinson motor triad",
      stem: "Which triad should the nurse recognize as idiopathic Parkinson disease?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "Resting tremor, rigidity, and bradykinesia", correct: true },
        { text: "Fever, nuchal rigidity, and photophobia", correct: false },
        { text: "Ptosis, miosis, and anhidrosis", correct: false },
        { text: "Intention tremor, nystagmus, and scanning speech", correct: false },
      ],
    },
  },
  {
    match: /name the site marked with an arrow for an intramuscular injection/i,
    item: {
      title: "Adult IM site",
      stem: "Name the site marked with an arrow for an intramuscular injection in an adult.",
      blooms: "Remembering",
      difficulty: "Easy",
      choices: [
        { text: "Ventrogluteal site", correct: true },
        { text: "Dorsogluteal site", correct: false },
        { text: "Deltoid only in a neonate", correct: false },
        { text: "Subcutaneous abdomen", correct: false },
      ],
    },
  },
  {
    match: /older adult is admitted after a fall\. what does the nurse do first/i,
    item: {
      title: "Fall risk on admission",
      stem: "An older adult is admitted after a fall. What does the nurse do first?",
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: "Complete a fall risk assessment", correct: true },
        { text: "Order a walking frame", correct: false },
        { text: "Teach home hazard removal", correct: false },
        { text: "Refer to physiotherapy", correct: false },
      ],
    },
  },
  {
    match: /vital sign change is expected in a healthy adult during moderate exercise/i,
    item: {
      title: "Exercise vital signs",
      stem: "Which vital sign change is expected in a healthy adult during moderate exercise?",
      blooms: "Understanding",
      difficulty: "Medium",
      choices: [
        { text: "Both systolic and diastolic fall", correct: false },
        { text: "Systolic falls, diastolic rises", correct: false },
        { text: "Systolic pressure rises, diastolic stays near baseline", correct: true },
        { text: "Neither changes", correct: false },
      ],
    },
  },
]

const SUBJECT_KEYS: Record<
  string,
  { correct: string; wrong: [string, string, string] }
> = {
  "atrial fibrillation": {
    correct: "An irregularly irregular pulse",
    wrong: ["A regular wide-complex tachycardia", "A fixed split S2", "Pulsus paradoxus only"],
  },
  "heart failure": {
    correct: "Dyspnea with pulmonary crackles",
    wrong: ["Unilateral calf swelling only", "A painless thyroid nodule", "Isolated tinnitus"],
  },
  "chest pain": {
    correct: "Pressure radiating to the arm or jaw",
    wrong: ["Pain that is worse only with swallowing", "Pain relieved by eating", "Pain that follows a dermatomal rash"],
  },
  "stroke recognition": {
    correct: "Sudden facial droop and arm weakness",
    wrong: ["Gradual hearing loss over years", "Isolated nail-clubbing", "A painless goiter"],
  },
  "seizure precautions": {
    correct: "Padded rails and suction at the bedside",
    wrong: ["A tight tongue blade between the teeth", "Restraints in four points at all times", "A dark room with no monitoring"],
  },
  "cranial nerve assessment": {
    correct: "Extraocular movement and facial symmetry",
    wrong: ["Ankle jerk only", "Capillary refill in the toes", "Abdominal girth"],
  },
  "acute dyspnea": {
    correct: "Work of breathing and oxygen saturation",
    wrong: ["Nail length", "Hair texture", "Ear canal wax"],
  },
  "oxygen titration": {
    correct: "Titrate to the ordered saturation target",
    wrong: ["Remove oxygen to test tolerance first", "Set the flow by client preference only", "Hold oxygen during meals always"],
  },
  pneumonia: {
    correct: "Fever with productive cough and crackles",
    wrong: ["Painless jaundice only", "A painless testicular mass", "Isolated night blindness"],
  },
  "warfarin monitoring": {
    correct: "INR",
    wrong: ["Hemoglobin A1c", "Serum amylase", "Urine specific gravity only"],
  },
  "opioid safety": {
    correct: "Count the respiratory rate before each dose",
    wrong: ["Give the next dose early if the client is drowsy", "Skip the count when the client is asleep", "Double the dose for breakthrough pain"],
  },
  "antibiotic selection": {
    correct: "Obtain cultures before the first dose when the client is stable",
    wrong: ["Start every antibiotic without a source", "Stop all antibiotics after one dose", "Give two beta-lactams for every fever"],
  },
  "sepsis recognition": {
    correct: "Fever, tachycardia, and hypotension",
    wrong: ["An isolated dry cough", "A single loose stool", "Mild seasonal rhinitis"],
  },
  "next diagnostic step": {
    correct: "The study that changes the next decision",
    wrong: ["Every available scan at once", "No further testing after one vital sign", "Discharge without a working diagnosis"],
  },
  "problem representation": {
    correct: "A one-sentence summary of the syndrome",
    wrong: ["A list of every past medication", "The room number only", "The family's preferred name"],
  },
  "troponin trend": {
    correct: "A serial rise after myocardial injury",
    wrong: ["A falling creatinine only", "A single random glucose", "A stable hemoglobin"],
  },
  "electrolyte imbalance": {
    correct: "Potassium outside the safe range",
    wrong: ["A normal serum sodium", "A single low-normal glucose", "A slightly warm forehead"],
  },
  "acid-base change": {
    correct: "Interpret the arterial blood gas pattern",
    wrong: ["Check only the heart rate", "Weigh the client once", "Inspect the nails"],
  },
  "fall risk": {
    correct: "Complete a fall risk assessment first",
    wrong: ["Order a walking frame before assessment", "Teach home hazards first", "Refer to physiotherapy first"],
  },
  "medication error": {
    correct: "Stop, assess the client, and report",
    wrong: ["Hide the event from the record", "Give a second dose to correct it", "Wait until the next shift"],
  },
  "two patient identifiers": {
    correct: "Name and date of birth",
    wrong: ["Room number only", "Bed number only", "The color of the gown"],
  },
  "informed consent": {
    correct: "The client can state the procedure and its risks",
    wrong: ["A relative signs while the client is uninformed", "Consent after the procedure", "A nurse signs in place of the client"],
  },
  confidentiality: {
    correct: "Do not discuss the client in a public space",
    wrong: ["Share the record in a hallway", "Post the diagnosis on social media", "Leave the chart open on a bus"],
  },
  documentation: {
    correct: "Record only what was assessed or done",
    wrong: ["Chart care that has not happened yet", "Leave blanks for another shift to invent", "Copy another client's note"],
  },
  "teach-back": {
    correct: "Ask the client to repeat the plan",
    wrong: ["Hand a pamphlet and leave", "Ask only yes or no questions", "Teach the roommate instead"],
  },
  "discharge teaching": {
    correct: "Warning signs and who to call",
    wrong: ["No follow-up plan", "A list of foods to avoid only", "The hospital gift-shop hours"],
  },
  "difficult news": {
    correct: "A private space and time to ask questions",
    wrong: ["Tell the client in a hallway", "Leave a voicemail only", "Ask a visitor to pass it on"],
  },
  "unstable blood pressure": {
    correct: "Activate the rapid response team",
    wrong: ["Document and recheck in two hours", "Encourage a long walk", "Give a hypnotic to settle the client"],
  },
  anaphylaxis: {
    correct: "Intramuscular epinephrine now",
    wrong: ["An oral antihistamine only", "A warm blanket and observation", "A nebulizer of saline only"],
  },
  "time-critical chest pain": {
    correct: "Obtain an ECG within 10 minutes",
    wrong: ["Wait for morning rounds", "Send the client home for rest", "Give an antacid and discharge"],
  },
  "pediatric fever": {
    correct: "Age-adjusted vital signs and hydration",
    wrong: ["Adult dosing of every drug", "No fluids until the fever breaks", "Aspirin for every viral illness"],
  },
  "growth concerns": {
    correct: "Plot height and weight on the growth chart",
    wrong: ["Skip measurements for one visit", "Compare only to adult norms", "Reassure without plotting"],
  },
  "age-specific dosing": {
    correct: "Calculate the dose by weight",
    wrong: ["Use the adult tablet as written", "Guess from the child's age only", "Double the last home dose"],
  },
  "prenatal warning signs": {
    correct: "Vaginal bleeding or a severe headache",
    wrong: ["Mild ankle swelling at the end of the day", "Fetal movement that is unchanged", "Heartburn after a large meal"],
  },
  "contraception counseling": {
    correct: "A method that fits the medical history",
    wrong: ["The same method for every client", "No discussion of contraindications", "Advice based on rumor only"],
  },
  "postpartum assessment": {
    correct: "Fundus, lochia, and bleeding",
    wrong: ["Only the infant's weight", "Only the partner's questions", "Only the meal tray"],
  },
  "insulin resistance": {
    correct: "Rising glucose despite the usual insulin",
    wrong: ["A single low reading after a snack", "A normal fasting glucose", "A falling A1c without change"],
  },
  hypoglycemia: {
    correct: "Give 15 g of fast carbohydrate",
    wrong: ["Give more insulin now", "Withhold all carbohydrate", "Encourage vigorous exercise first"],
  },
  "thyroid storm": {
    correct: "Fever, tachycardia, and altered mentation",
    wrong: ["Bradycardia and hypothermia", "Isolated constipation", "A painless goiter only"],
  },
  "gram-negative sepsis": {
    correct: "Early fluids and cultures",
    wrong: ["Wait for the culture before any support", "A diuretic first", "Discharge with oral fluids only"],
  },
  "microbiology culture results": {
    correct: "Treat from the sensitivity report",
    wrong: ["Ignore the report and keep every drug", "Stop all therapy when the culture is sent", "Add every listed antibiotic at once"],
  },
  "isolation precautions": {
    correct: "Match the precaution to the organism",
    wrong: ["No isolation for any diarrhea", "Airborne precautions for every fever", "Protective isolation for C. difficile"],
  },
  "nursing assessment": {
    correct: "Airway, breathing, and circulation first",
    wrong: ["Complete the full social history first", "Start teaching before vital signs", "Delegate assessment to a visitor"],
  },
}

const GENERATED_STEM: { match: RegExp; subject: (match: RegExpMatchArray) => string }[] = [
  {
    match: /^A client is admitted with (.+)\. Which assessment finding is expected\?$/i,
    subject: (match) => match[1] ?? "",
  },
  {
    match: /^A client asks why (.+) matters\. Which explanation is accurate\?$/i,
    subject: (match) => match[1] ?? "",
  },
  {
    match: /^A client presents with (.+)\. Which action is the priority\?$/i,
    subject: (match) => match[1] ?? "",
  },
  {
    match: /^Which assessment finding best distinguishes (.+) from a similar presentation\?$/i,
    subject: (match) => match[1] ?? "",
  },
  {
    match: /^A client with (.+) has conflicting orders\. Which plan is most appropriate\?$/i,
    subject: (match) => match[1] ?? "",
  },
  {
    match: /^A client is at risk for (.+)\. Which monitoring plan is indicated\?$/i,
    subject: (match) => match[1] ?? "",
  },
]

function generatedItemForStem(stem: string): AuthoredItem | null {
  const text = stem.trim()
  for (const row of GENERATED_STEM) {
    const match = text.match(row.match)
    if (!match) continue
    const subject = row.subject(match).trim().toLowerCase()
    const key = SUBJECT_KEYS[subject]
    if (!key) return null
    return {
      title: subject,
      stem: text,
      blooms: "Applying",
      difficulty: "Medium",
      choices: [
        { text: key.correct, correct: true },
        { text: key.wrong[0], correct: false },
        { text: key.wrong[1], correct: false },
        { text: key.wrong[2], correct: false },
      ],
    }
  }
  return null
}

export function authoredItemForStem(stem: string): AuthoredItem | null {
  const text = stem.trim()
  if (!text) return null
  return CATALOG.find((row) => row.match.test(text))?.item ?? generatedItemForStem(text)
}

export function isPacemakerFamilyStem(stem: string): boolean {
  if (isTelemetryPacemakerStem(stem)) return true
  const item = authoredItemForStem(stem)
  return (
    item === PACEMAKER_TELEMETRY ||
    item === PACEMAKER_ATHLETE ||
    item === PACEMAKER_HEART_BLOCK ||
    item === PACEMAKER_JUNCTIONAL
  )
}

export function authoredChoicesForStem(stem: string): AuthoredChoice[] | null {
  const item = authoredItemForStem(stem)
  return item ? item.choices.map((choice) => ({ ...choice })) : null
}

export function hasAuthoredKey(stem: string): boolean {
  return (
    authoredItemForStem(stem) != null || authoredFillBlankForStem(stem) != null
  )
}

/** When the stem names the physiology, mark that option even if the letter was hashed. */
export function correctIndexForChoices(
  stem: string,
  options: readonly string[],
): number | null {
  const authored = authoredChoicesForStem(stem)
  if (authored) {
    const correct = authored.find((choice) => choice.correct)
    if (correct) {
      const match = options.findIndex((option) => norm(option) === norm(correct.text))
      if (match >= 0) return match
    }
  }

  const text = stem.toLowerCase()
  const find = (name: RegExp) =>
    options.findIndex((option) => name.test(option))

  if (
    /p wave before each qrs|upright p wave|primary source of electrical impulse|setting this rate/.test(
      text,
    )
  ) {
    const index = find(/sinoatrial|\bsa node\b/)
    if (index >= 0) return index
  }
  if (
    /dissociated|ventricular (rate|contractions) at 3|wide qrs|0\.16 seconds/.test(
      text,
    ) &&
    /ventricular/.test(text)
  ) {
    const index = find(/purkinje/)
    if (index >= 0) return index
  }
  if (/no (visible )?p waves/.test(text) && /narrow qrs/.test(text)) {
    const index = find(/atrioventricular|\bav node\b/)
    if (index >= 0) return index
  }
  if (/ii, iii, and avf|inferior stemi/.test(text)) {
    const index = find(/right coronary|inferior/)
    if (index >= 0) return index
  }
  if (/radiates to the carotids|right upper sternal border/.test(text)) {
    const index = find(/aortic stenosis|aortic valve/)
    if (index >= 0) return index
  }
  if (/warfarin/.test(text) && /laboratory|adjust/.test(text)) {
    const index = find(/\binr\b|international normalized/)
    if (index >= 0) return index
  }

  return null
}

export function authoredFillBlankForStem(stem: string): string[] | null {
  if (
    /anterior-wall stemi is produced by occlusion|anterior descending/i.test(stem)
  ) {
    return ["left anterior descending", "LAD", "left anterior descending artery"]
  }
  if (/hand hygiene with soap and water must last/i.test(stem)) {
    return ["20", "twenty"]
  }
  // Two wordings of the facial-nerve item, and the blank is not in the same
  // place in both. "…innervated by cranial nerve ___" wants the numeral;
  // "The ___ nerve …" wants the name. Accepting either list for either stem
  // would mark "cranial nerve facial" correct.
  if (/innervated by cranial nerve/i.test(stem)) {
    return ["VII", "7", "CN VII", "seventh"]
  }
  if (/motor control of facial expression/i.test(stem)) {
    return ["facial", "CN VII", "seventh cranial nerve"]
  }
  if (/normal resting respiratory rate/i.test(stem)) {
    return ["12", "20"]
  }
  return null
}

export function pacemakerLeoReplace(): AuthoredItem {
  return PACEMAKER_REPLACE
}

export function pacemakerLeoSuggestions(existingStems: readonly string[]): AuthoredItem[] {
  const taken = new Set(existingStems.map((stem) => norm(stem)))
  return PACEMAKER_SUGGESTIONS.filter((item) => !taken.has(norm(item.stem)))
}
